import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon, Button, Input, Label } from '../ui';
import { useAuth } from '../../contexts/AuthContext';
import fullLogo from '../../assets/full_logo.png';

export default function LoginOverlay({ onClose }) {
  const [view, setView] = React.useState('login');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState(null);
  const [successMsg, setSuccessMsg] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await login(email, password);
      if (result.success) {
        const user = result.user;
        if (user?.permissions?.globalSuperAdmin) navigate('/admin/tenants', { replace: true });
        else if (user?.role === 'client') navigate('/client/dashboard', { replace: true });
        else navigate('/dashboard', { replace: true });
      } else {
        setError(result.error || 'Erreur de connexion');
      }
    } catch (err) {
      setError('Erreur inattendue lors de la connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccessMsg("Si cet email existe, un lien a été envoyé.");
        setTimeout(() => setView('login'), 5000);
      } else {
        setError(data.message || "Impossible d'envoyer l'email.");
      }
    } catch (err) {
      setError("Erreur serveur.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -10 }}
        className="absolute top-24 right-6 w-96 z-50"
      >
        <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl p-8 relative overflow-hidden">
          <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors">
            <Icon name="IconX" size={20} />
          </button>

          <div className="flex flex-col items-center mb-8">
            <div className="bg-white p-2 rounded-xl shadow-sm border border-slate-100 mb-3">
              <img src={fullLogo} alt="AccountInTime" className="h-10 w-auto object-contain" />
            </div>
            <h2 className="text-lg font-semibold text-slate-700">
              {view === 'login' ? 'Connexion sécurisée' : 'Mot de passe oublié'}
            </h2>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg flex items-center gap-2">
              <Icon name="IconAlertCircle" size={16} className="flex-shrink-0" />
              {error}
            </div>
          )}
          {successMsg && (
            <div className="mb-4 p-3 bg-green-50 border border-green-100 text-green-600 text-sm rounded-lg flex items-center gap-2">
              <Icon name="IconCheckCircle" size={16} className="flex-shrink-0" />
              {successMsg}
            </div>
          )}

          {view === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label required>Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <Label required>Mot de passe</Label>
                  <button type="button" onClick={() => setView('forgot')} className="text-xs text-indigo-600 hover:text-indigo-800 font-bold">
                    Oublié ?
                  </button>
                </div>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <Button type="submit" disabled={loading} className="w-full justify-center py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white">
                {loading ? 'Connexion...' : 'Accéder au compte'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <p className="text-sm text-slate-500 mb-2">Entrez votre email pour réinitialiser.</p>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <Button type="submit" disabled={loading} className="w-full justify-center py-3 bg-slate-800 text-white">
                {loading ? 'Envoi...' : 'Envoyer le lien'}
              </Button>
              <button type="button" onClick={() => setView('login')} className="w-full py-2 text-sm text-slate-500 hover:text-indigo-600 font-medium transition-colors">
                <Icon name="IconArrowLeft" size={14} className="inline mr-1" />
                Retour
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
